import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import React from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "src/components/base/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { ExclamationTriangleIcon, InformationCircleIcon, PencilSquareIcon } from "@heroicons/react/20/solid";
import { Badge } from "src/components/base/badges/base";

/**
 * The properties for {@link OidcSettings}
 */
type OidcSettingsProps = {};

/**
 * The OIDC settings page
 */
function OidcSettings(props: OidcSettingsProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();

    const router = useRouter();
    const settings = Route.useLoaderData();

    return (
        <Table dense={true}>
            <TableHead>
                <TableRow>
                    <TableCell></TableCell>
                    <TableCell>{t("label.enable-oidc")}</TableCell>
                    <TableHeader>{t("label.warnings")}</TableHeader>
                    <TableHeader className={"w-0"}>
                        <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                    </TableHeader>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow href={"/a/settings/oidc/trufflepig"}>
                    <TableCell>{t("heading.trufflepig-oidc")}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell>
                        <InformationCircleIcon className={"size-5"} />
                    </TableCell>
                </TableRow>
                <TableRow href={"/a/settings/oidc/customer/admin"}>
                    <TableCell>{t("heading.frontend-oidc")}</TableCell>
                    <TableCell>
                        <Switch
                            color={"blue"}
                            checked={settings.customer_frontend.enabled}
                            onChange={async (enabled) => {
                                await ApiInternal.settings.updateOidc({
                                    customer_frontend: {
                                        ...settings.customer_frontend,
                                        enabled,
                                    },
                                });
                                router.invalidate();
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <div className={"w-24"}>
                            {settings.customer_frontend.enabled &&
                                (settings.customer_frontend.client_secret.length === 0 ||
                                    settings.customer_frontend.url.length === 0 ||
                                    settings.customer_frontend.redirect_url.length === 0 ||
                                    settings.customer_frontend.client_id.length === 0) && (
                                    <Badge color={"yellow"}>
                                        <ExclamationTriangleIcon className={"size-4"} />
                                        {t("label.missing-configuration")}
                                    </Badge>
                                )}
                        </div>
                    </TableCell>
                    <TableCell>
                        <PencilSquareIcon className={"size-5"} />
                    </TableCell>
                </TableRow>
                <TableRow href={"/a/settings/oidc/customer/vpn"}>
                    <TableCell>{t("heading.tailscale-oidc")}</TableCell>
                    <TableCell>
                        <Switch
                            color={"blue"}
                            checked={settings.customer_tailscale.enabled}
                            onChange={async (enabled) => {
                                await ApiInternal.settings.updateOidc({
                                    customer_tailscale: {
                                        ...settings.customer_tailscale,
                                        enabled,
                                    },
                                });
                                router.invalidate();
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <div className={"w-24"}>
                            {settings.customer_tailscale.enabled &&
                                (settings.customer_tailscale.client_secret.length === 0 ||
                                    settings.customer_tailscale.url.length === 0 ||
                                    settings.customer_tailscale.redirect_url.length === 0 ||
                                    settings.customer_tailscale.client_id.length === 0) && (
                                    <Badge color={"yellow"}>
                                        <ExclamationTriangleIcon className={"size-4"} />
                                        {t("label.missing-configuration")}
                                    </Badge>
                                )}
                        </div>
                    </TableCell>
                    <TableCell>
                        <PencilSquareIcon className={"size-5"} />
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/oidc/")({
    component: OidcSettings,
    loader: async () => await ApiInternal.settings.getOidc(),
});
